import { useEffect } from 'react'

import { QUINCE } from '@src/quince-site-common/src/constants/app'
import Product from '@src/quince-site-common/src/domains/Product'
import { ProductOptionName } from '@src/quince-site-common/src/domains/ProductOption'
import { BaseProductVariant, ProductVariantOption } from '@src/quince-site-common/src/domains/ProductVariant'
import productHelper from '@src/quince-site-common/src/helpers/productHelper'
import usePrevious from '@src/quince-site-common/src/hooks/usePrevious'
import { trackPageViewForProduct } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { isSearchResult } from '@src/quince-site-common/src/services/algoliaService/utils'
import { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'
import { FINDING_METHOD_AGGREGATE, getCurrentFindingMethod, setFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTracking } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { isEqual } from '@src/utils/functions/utils'

export const useMiniPDPProductTracker = ({
  product,
  selectedOptions,
  selectedVariant,
  sectionData,
}: {
  product: Product
  selectedOptions: ProductVariantOption[]
  selectedVariant?: BaseProductVariant
  sectionData: TPDPSectionData
}) => {
  const previousProduct = usePrevious(product)
  const variantId = selectedVariant?.svid?.toString()
  const productProperties = getProductPropertiesForTracking(product, variantId, { is_minipdp: true })

  useEffect(() => {
    const currentProductID = product.spid
    const previousProductID = previousProduct?.spid
    if (!isEqual(currentProductID, previousProductID)) {
      const applicableVariants = productHelper.getVariantsForOptions(product, selectedOptions)
      const { svid, title, sku, price, options, id } =
        productHelper.getVariantWithMinQuincePriceForOptions(product, selectedOptions) ?? {}
      const variantColor = options?.filter(t => t.name === ProductOptionName.Color)[0]?.value ?? ''
      const variantsImages = sectionData.colorValueProductImagesMap[variantColor]
      const currentFindingMethod = getCurrentFindingMethod()
      trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_VIEW, {
        productId: product.spid,
        isValentineProduct: product.isValentineProduct,
        internalProductId_color: product.id.toString() + '_' + (variantColor || ''),
        variantId: svid,
        variantTitle: title,
        sku,
        productTitle: product.title,
        price,
        productType: product.productTypeName,
        vendor: QUINCE,
        variantImageUrl: variantsImages?.[0]?.image?.url,
        division: product.classification?.division?.name,
        subDepartment: product.classification?.subDepartment?.name,
        department: product.classification?.department?.name,
        class: product.classification?.class?.name,
        subClass: product.classification?.subClass?.name,
        gender: product.gender,
        variantIds: applicableVariants.map(v => v.svid),
        internal_variant_id: id,
        internal_product_id: product.id,
        slug: product.slug,
        context: {
          findingMethodAggregate: currentFindingMethod?.findingMethodAggregate,
          findingMethodGranular: currentFindingMethod?.findingMethodGranular,
          findingMethodId: currentFindingMethod?.findingMethodId,
          productProperties,
        },
        isMiniPdp: true,
      })
      setFindingMethod[FINDING_METHOD_AGGREGATE.LANDING_PAGE]?.()
      if (!isSearchResult())
        trackPageViewForProduct({
          objectID: `${product.spid}_${selectedOptions[0]?.value}`,
        })
    }
  }, [product.id, product])
}
